import { mapState, mapGetters } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import { LngLatBounds } from 'mapbox-gl';
import twinFields from '../twinFields';
import TwinMapMarker from './twinMapMarker.vue';
import { vuetifyColors as colorMap, adjustColorLightness, getContrastYIQ } from '../../../../utils/themes';
import debounce from 'lodash/debounce';
import vuetifyColors from 'vuetify/lib/util/colors';
const {
  evaluateTemplate,
  getDefinitions: getTwinFields
} = twinFields;
export default {
  name: 'TwinMap',
  components: {
    TwinMapMarker
  },
  props: {
    widget: {
      type: Object,
      required: true
    },
    data: {
      type: Object,
      required: false,
      default: () => ({})
    },
    paddingTop: {
      type: Number,
      required: false,
      default: 50
    },
    customParameters: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
  data() {
    return {
      twinsWithCoordinates: [],
      interactive: false,
      map: null,
      unclusteredTwins: [],
      clusterColor: null,
      clusterTextColor: null
    };
  },
  computed: {
    ...mapGetters({
      isFeatureFlagEnabled: 'app/isFeatureFlagEnabled'
    }),
    ...mapState('app', ['color', 'colorHexLight', 'colorHexDark', 'UiTheme']),
    pitch() {
      return this.$store.state.app.map3DBuild ? 45 : 0;
    },
    twinMapData() {
      return cloneDeep(get(this.data, 'twin-map', []));
    },
    twinFieldDefinitions() {
      return getTwinFields(this.$t, this.twinsWithCoordinates);
    },
    maxZoom() {
      return get(this.widget, 'twinMapOptions.maxZoom', 15);
    },
    clusterRadius() {
      return get(this.widget, 'twinMapOptions.clusterRadius', 25);
    },
    hasAttributes() {
      return get(this.widget, 'twinMapOptions.attributes', []).length > 0;
    },
    hasActions() {
      return get(this.widget, 'twinMapOptions.actions', []).length > 0;
    }
  },
  watch: {
    widget: {
      handler() {
        const completeWidget = cloneDeep(this.widget);
        if (!completeWidget.twinMapOptions) {
          completeWidget.twinMapOptions = {};
        }
        this.interactive = completeWidget.twinMapOptions.interactive || false;
      },
      deep: true,
      immediate: true
    },
    color: {
      handler() {
        const color = colorMap[this.color];
        const customerColor = this.UiTheme === 'light' ? this.colorHexLight : this.colorHexDark;
        this.clusterColor = color || customerColor;
        this.clusterTextColor = getContrastYIQ(this.clusterColor, '#333', '#fff', 120);
        this.updateClusterColors();
      },
      immediate: true
    },
    twinMapData: {
      handler(newMapData, oldMapData) {
        let mapData = newMapData;
        if (oldMapData) {
          if (JSON.stringify(newMapData) === JSON.stringify(oldMapData)) {
            return;
          }
        }
        this.twinsWithCoordinates = mapData.filter(twin => twin.coordinates);
      },
      deep: true,
      immediate: true
    },
    async map() {
      this.zoom();
    },
    twinsWithCoordinates: {
      handler() {
        this.zoom();
        this.setupClustering();
      },
      deep: true
    },
    '$store.state.app.map3DBuild': {
      handler() {
        this.updateMapPitch();
      }
    },
    'widget.twinMapOptions.tilesetIds': {
      handler(newTilesetIds, oldTilesetIds) {
        if (!this.isFeatureFlagEnabled('rd-7817-shapefiles')) {
          return;
        }
        if (oldTilesetIds) {
          const removedTilesetIds = oldTilesetIds.filter(tilesetId => !newTilesetIds.includes(tilesetId));
          this.removeTilesetsFromMap(removedTilesetIds);
        }
        this.addTilesetsToMap();
      },
      immediate: true
    },
    maxZoom() {
      this.zoom();
    }
  },
  methods: {
    get,
    cloneDeep,
    async onMapLoaded() {
      this.zoom();
      this.setupClustering();
      this.addTilesetsToMap();
    },
    onMapResized() {
      this.zoom();
    },
    zoom() {
      if (!this.$refs.mapGl || !this.$refs.mapGl.map) {
        return;
      }
      const actualMap = this.$refs.mapGl.map;
      if (!actualMap) {
        return;
      }
      if (this.twinsWithCoordinates.length === 0) {
        return;
      }
      const bounds = new LngLatBounds();
      this.twinsWithCoordinates.forEach(twin => {
        const coords = cloneDeep(twin.coordinates.coordinates);
        bounds.extend(coords);
      });
      const zoomingDisabled = get(this.widget, 'twinMapOptions.zoomingDisabled', false);
      actualMap.fitBounds(bounds, {
        padding: {
          top: this.paddingTop,
          bottom: 50,
          left: 50,
          right: 50
        },
        maxZoom: this.maxZoom,
        pitch: this.pitch,
        easing: val => {
          if (zoomingDisabled) {
            return 1;
          }
          return Math.min(1, val * 3);
        }
      });
    },
    updateMapPitch() {
      if (!this.$refs.mapGl || !this.$refs.mapGl.map) {
        return;
      }
      this.$refs.mapGl.map.easeTo({
        pitch: this.pitch
      });
    },
    addTilesetsToMap: debounce(function () {
      this._addTilesetsToMap();
    }, 300),
    async _addTilesetsToMap() {
      const map = get(this.$refs, 'mapGl.map');
      if (!map) {
        return;
      }
      const tilesetIds = get(this.widget, 'twinMapOptions.tilesetIds', []);
      const colorsList = Object.values(vuetifyColors);
      for (const tilesetId of tilesetIds) {
        if (map.getLayer(tilesetId)) {
          return;
        }
        try {
          const existingSource = map.getSource(tilesetId);
          if (!existingSource) {
            map.addSource(tilesetId, {
              type: 'vector',
              url: `mapbox://${tilesetId}`
            });
          }
          while (!map.isSourceLoaded(tilesetId)) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
          const source = await map.getSource(tilesetId);
          get(source, 'vectorLayers', []).forEach((layer, index) => {
            const colorIndex = index * 14 % colorsList.length;
            const color = colorsList[colorIndex].base;
            map.addLayer({
              id: `${tilesetId}-${layer.id}`,
              type: 'line',
              source: layer.source,
              'source-layer': layer.id,
              paint: {
                'line-color': color,
                'line-width': 3
              }
            });
          });
        } catch (error) {
          console.error('error adding tileset to map', tilesetId, error);
        }
      }
    },
    async removeTilesetsFromMap(tilesetIds) {
      if (!this.$refs.mapGl || !this.$refs.mapGl.map) {
        return;
      }
      const map = this.$refs.mapGl.map;
      for (const tilesetId of tilesetIds) {
        while (!map.isSourceLoaded(tilesetId)) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
        const source = await map.getSource(tilesetId);
        for (const layer of get(source, 'vectorLayers', [])) {
          try {
            map.removeLayer(`${tilesetId}-${layer.id}`);
          } catch (error) {
            console.error(error);
          }
        }
      }
    },
    evaluateTemplate(twin, template) {
      return evaluateTemplate({
        i18n_t: this.$t,
        twin,
        template,
        customParameters: this.customParameters
      });
    },
    getOffsetForOverlappingMarkers(map, point, index) {
      if (!map) {
        return {};
      }
      let offsetX = 0;
      let offsetY = 0;

      // find markers to close to this (they most likely overlap)
      const overlappingMarkers = this.unclusteredTwins.filter((otherTwin, otherIndex) => {
        if (otherIndex === index) {
          return false;
        }
        const otherPoint = map.project(otherTwin.coordinates.coordinates);
        const distance = Math.sqrt(Math.pow(point.x - otherPoint.x, 2) + Math.pow(point.y - otherPoint.y, 2));
        return distance < 28;
      });

      // calculate offset only if there are actuallyoverlapping markers
      if (overlappingMarkers.length > 0) {
        const offsetRadius = 12;
        const angle = index * 137.5 % 360;
        offsetX = Math.cos(angle * Math.PI / 180) * offsetRadius;
        offsetY = Math.sin(angle * Math.PI / 180) * offsetRadius;
      }
      return [offsetX, offsetY];
    },
    getMarkerStyle(twin, index) {
      if (!this.$refs.mapGl || !this.$refs.mapGl.map) {
        return {};
      }
      const map = this.$refs.mapGl.map;
      const point = map.project(twin.coordinates.coordinates);
      const [offsetX, offsetY] = this.getOffsetForOverlappingMarkers(map, point, index);
      return {
        position: 'absolute',
        left: `${point.x + offsetX}px`,
        top: `${point.y + offsetY}px`,
        transform: 'translate(-50%, -50%)',
        // zIndex: 1000,
        borderRadius: '50%',
        pointerEvents: this.hasAttributes || this.hasActions ? 'auto' : 'none',
        cursor: this.hasAttributes || this.hasActions ? 'pointer' : 'default'
      };
    },
    setupClustering() {
      if (!this.$refs.mapGl || !this.$refs.mapGl.map) {
        return;
      }
      const map = this.$refs.mapGl.map;
      const sourceData = {
        type: 'FeatureCollection',
        features: this.twinsWithCoordinates.map(twin => ({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: twin.coordinates.coordinates
          },
          properties: {
            id: twin.id,
            ...twin.properties
          }
        }))
      };

      // if source exists update it, otherwise add it
      if (map.getSource('twins')) {
        map.getSource('twins').setData(sourceData);
      } else {
        map.addSource('twins', {
          type: 'geojson',
          data: sourceData,
          cluster: true,
          clusterMaxZoom: 14,
          clusterRadius: this.clusterRadius
        });
        map.addLayer({
          id: 'clusters',
          type: 'circle',
          source: 'twins',
          filter: ['has', 'point_count'],
          paint: {
            'circle-color': ['step', ['get', 'point_count'], adjustColorLightness(this.clusterColor, -10), 10, adjustColorLightness(this.clusterColor, 0), 30, adjustColorLightness(this.clusterColor, 10)],
            'circle-radius': ['step', ['get', 'point_count'], 20, 10, 25, 30, 30]
          }
        });
        map.addLayer({
          id: 'cluster-count',
          type: 'symbol',
          source: 'twins',
          filter: ['has', 'point_count'],
          layout: {
            'text-field': '{point_count_abbreviated}',
            'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
            'text-size': 16
          },
          paint: {
            'text-color': this.clusterTextColor
          }
        });
        map.addLayer({
          id: 'unclustered-point',
          type: 'circle',
          source: 'twins',
          filter: ['!', ['has', 'point_count']],
          paint: {
            'circle-radius': 0
          }
        });

        // commented out because unclustered markers will
        // not show up when zooming in through cluster click

        // add click handler for clusters to zoom in
        // map.on('click', 'clusters', (e) => {
        //   const features = map.queryRenderedFeatures(e.point, {
        //     layers: ['clusters'],
        //   });
        //   const clusterId = features[0].properties.cluster_id;
        //   const source = map.getSource('twins');
        //   source.getClusterExpansionZoom(clusterId, (err, zoom) => {
        //     if (err) {
        //       return;
        //     }
        //     map.easeTo({
        //       center: features[0].geometry.coordinates,
        //       zoom: zoom,
        //     });
        //   });
        // });

        map.on('move', this.updateUnclusteredPoints);
        map.on('zoom', this.updateUnclusteredPoints);
      }
      this.updateUnclusteredPoints();
    },
    updateUnclusteredPoints() {
      if (!this.$refs.mapGl || !this.$refs.mapGl.map) {
        return;
      }
      const map = this.$refs.mapGl.map;
      if (!map) {
        return;
      }

      // get all currently rendeed map features for current viewport
      const features = map.queryRenderedFeatures({
        layers: ['unclustered-point']
      });
      this.unclusteredTwins = features.map(feature => this.twinsWithCoordinates.find(twin => twin.id === feature.properties.id)).filter(Boolean);
    },
    updateClusterColors() {
      var _this$$refs$mapGl;
      const map = (_this$$refs$mapGl = this.$refs.mapGl) === null || _this$$refs$mapGl === void 0 ? void 0 : _this$$refs$mapGl.map;
      if (!map) {
        return;
      }
      if (map.getLayer('clusters')) {
        map.setPaintProperty('clusters', 'circle-color', ['step', ['get', 'point_count'], adjustColorLightness(this.clusterColor, -10), 10, adjustColorLightness(this.clusterColor, 0), 30, adjustColorLightness(this.clusterColor, 10)]);
      }
      if (map.getLayer('cluster-count')) {
        map.setPaintProperty('cluster-count', 'text-color', this.clusterTextColor);
      }
    }
  }
};